import React, { useEffect, useMemo, useState } from 'react';
import { capitalize, sortBy, toLower } from 'lodash';

import useGetProgramRules from 'Queries/Hooks/ProgramRule/useGetProgramRules';
import {
	DataIndex, indexByProperty, indexByPropertyArray, indexByPropertyMutate,
} from 'Util/DataIndexUtils';
import {
	CourseEntity,
	PlanSelectionEntity,
	ProgramRuleCourse,
	ProgramRuleEntity,
	RequirementEntity,
} from 'Models/Entities';
import {
	buildRequirementTree,
	RequirementNode,
	checkRequirementRulesViolation,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { flatten } from 'lodash';
import CourseAccordion from 'Views/Components/CourseAccordion';
import Chip from 'Views/Components/Chip/Chip';
import useScreenSize from 'Hooks/useScreenSize';
import CourseSelectionNotice from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelectionNotice';
import InfoIconButton from 'Views/Components/InfoIconButton/InfoIconButton';
import InfoModal from 'Views/Components/Modal/InfoModal';
import CompleteCreditChip from 'Views/Components/CourseAccordion/CompleteCreditChip';
import ChipGroup from 'Views/Components/Chip/ChipGroup';

interface ProgramRuleProps {
	programRules: ProgramRuleEntity[],
	planSelections: PlanSelectionEntity[],
	completedCourseIds: string[],
	description?: string,
	coursesById: DataIndex<CourseEntity>,
	getCourseComponents: (courseId: string) => string[];
}

const ProgramRule = (props: ProgramRuleProps) => {
	const {
		programRules,
		planSelections,
		completedCourseIds,
		description,
		coursesById,
		getCourseComponents,
	} = props;

	const isMobile = useScreenSize();

	const [
		showingRequirementDetails, setShowingRequirementDetails,
	] = useState<(RequirementEntity | ProgramRuleEntity) | null>(null);
	const [isCollapsed, setIsCollapsed] = useState<{ [key: string]: boolean }>({});

	const rootProgramRules = useMemo(() => {
		if (!programRules) {
			return [] as ProgramRuleEntity[];
		}
		return programRules.filter(programRule => !programRule.parentProgramRuleId);
	}, [programRules]);

	const programRulesByParentId: DataIndex<ProgramRuleEntity[]> = useMemo(() => {
		if (!programRules) {
			return {} as DataIndex<ProgramRuleEntity[]>;
		}

		return indexByPropertyArray(
			programRules,
			programRule => programRule.parentProgramRuleId || 'requirements',
		);
	}, [programRules]);

	useEffect(() => {
		if (!programRules) {
			return;
		}
		setIsCollapsed(indexByPropertyMutate(
			programRules,
			'id',
			() => isMobile,
		));
	}, [isMobile, programRules]);

	const programRuleCourseById = useMemo((): DataIndex<ProgramRuleCourse> => {
		if (!programRules) {
			return {} as DataIndex<ProgramRuleCourse>;
		}

		return indexByProperty(
			flatten(programRules.map(programRule => programRule.courses)),
			'id',
		);
	}, [programRules]);

	// Temp plan selections of the plan's selections that are mapped to program rule courses
	const mappedPlanSelections = useMemo(() => {
		const programRuleCourses = Object.values(programRuleCourseById);

		if (programRuleCourses.length === 0) {
			return [] as PlanSelectionEntity[];
		}

		return programRuleCourses.filter(offering => {
			const { courseId } = offering;

			return !!planSelections.find(selection => selection.courseId === courseId);
		}).map(programRuleCourse => new PlanSelectionEntity({
			courseId: programRuleCourse.courseId,
			locationId: programRuleCourse.programRuleId,
		}));
	}, [planSelections, programRuleCourseById]);

	const offeringIdsByCourseIdByRequirementId = useMemo(() => {
		const _offeringsByCourseIdByRequirementId: DataIndex<DataIndex<string[]>> = {};

		for (const { id, locationId, courseId } of Object.values(mappedPlanSelections)) {
			const existingCourses = _offeringsByCourseIdByRequirementId[locationId];
			if (!existingCourses) {
				_offeringsByCourseIdByRequirementId[locationId] = {};
			}

			const existingCourseOfferings = _offeringsByCourseIdByRequirementId[locationId][courseId];
			if (!existingCourseOfferings) {
				_offeringsByCourseIdByRequirementId[locationId][courseId] = [];
			}

			_offeringsByCourseIdByRequirementId[locationId][courseId].push(id);
		}

		return _offeringsByCourseIdByRequirementId;
	}, [mappedPlanSelections]);

	const requirementTree = useMemo(() => {
		if (!rootProgramRules.length && mappedPlanSelections.length === 0) {
			return {};
		}

		return buildRequirementTree(
			offeringIdsByCourseIdByRequirementId,
			rootProgramRules,
			programRulesByParentId,
			mappedPlanSelections,
			coursesById,
			programRuleCourseById,
		);
	}, [
		rootProgramRules,
		mappedPlanSelections,
		offeringIdsByCourseIdByRequirementId,
		coursesById,
		programRulesByParentId,
		programRuleCourseById,
	]);

	const renderRequirementAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		const errorMessage = hasViolatedRequirementRules
			? (
				<CourseAccordion.Label
					className="course-selection-notice"
					content={<CourseSelectionNotice />}
					isError
				/>
			)
			: undefined;

		return (
			<CourseAccordion.Accordion<RequirementNode>
				key={requirement.id}
				id={requirement.id}
				title={requirement.displayName}
				depth={depth}
				collapsed={isCollapsed[requirement.id]}
				setCollapsed={id => setIsCollapsed(prevState => {
					return {
						...prevState,
						[id]: !prevState[id],
					};
				})}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={requirementNode.childNodes}
				renderItem={item => {
					return renderRequirementTree(item, depth + 1);
				}}
				emptyLabel="No courses"
				description={
					requirement.description
						? <CourseAccordion.Label content={requirement.description} />
						: undefined
				}
				errorMessage={errorMessage}
				additionalGroupHeaderItems={(
					<InfoIconButton
						ariaLabel={`${requirement.displayName} requirement rules info`}
						hasError={hasViolatedRequirementRules}
						onClick={() => showRequirementInfoModal(requirement)}
					/>
				)}
			/>
		);
	};

	const renderCourseAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const courses = Object.keys(requirementNode.offeringIdsByCourseId)
			.map(courseId => coursesById[courseId])
			.sort((a, b) => a.code.localeCompare(b.code));

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		const errorMessage = hasViolatedRequirementRules
			? (
				<CourseAccordion.Label
					className="course-selection-notice"
					content={<CourseSelectionNotice />}
					isError
				/>
			)
			: undefined;

		return (
			<CourseAccordion.Accordion<CourseEntity>
				key={requirement.id}
				id={requirement.id}
				title={requirement.displayName}
				depth={depth}
				collapsed={isCollapsed[requirement.id]}
				setCollapsed={id => setIsCollapsed(prevState => {
					return {
						...prevState,
						[id]: !prevState[id],
					};
				})}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={courses}
				renderItem={(item, index) => {
					const courseComponents = getCourseComponents(item.id);

					return (
						<CourseAccordion.Item
							key={item.id}
							course={item}
							index={index}
							label={(
								<ChipGroup>
									{
										courseComponents.map(component => (
											<Chip key={component} chipLabel={component} />
										))
									}
								</ChipGroup>
							)}
							additionalStatus={
								completedCourseIds.find(id => id === item.id)
									? <CompleteCreditChip />
									: null
							}
						/>
					);
				}}
				emptyLabel="No courses"
				description={
					requirement.description
						? <CourseAccordion.Label content={requirement.description} />
						: undefined
				}
				errorMessage={errorMessage}
				additionalGroupHeaderItems={(
					<InfoIconButton
						ariaLabel={`show ${requirement.displayName} requirement rules`}
						hasError={hasViolatedRequirementRules}
						onClick={() => showRequirementInfoModal(requirement)}
					/>
				)}
			/>
		);
	};

	const renderRequirementTree = (requirementTreeNode: RequirementNode, depth: number) => {
		const { requirement } = requirementTreeNode;

		if (requirement.category === 'REQUIREMENTS') {
			return renderRequirementAccordion(requirementTreeNode, depth);
		}

		return renderCourseAccordion(requirementTreeNode, depth);
	};

	const showRequirementInfoModal = (requirement: RequirementEntity | ProgramRuleEntity) => {
		setShowingRequirementDetails(requirement);
	};

	const closeRequirementInfoModal = () => {
		setShowingRequirementDetails(null);
	};

	return (
		<>
			<h4 className="requirement-check-section-header">
				{capitalize(window.lexicon.program)} {toLower(window.lexicon.rules)}
			</h4>
			{
				description
					? <p>{description}</p>
					: null
			}
			{
				rootProgramRules.length > 0
				&& sortBy(requirementTree[rootProgramRules[0].id].childNodes, node => node.requirement.created)
					.map(requirementNode => renderRequirementTree(requirementNode, 0))
			}
			{ showingRequirementDetails ? (
				<InfoModal
					isOpen
					title={showingRequirementDetails.displayName}
					description={showingRequirementDetails.description}
					onCloseModal={closeRequirementInfoModal}
				/>
			) : null}
		</>
	);
};

export default ProgramRule;
